import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Groups as GroupsIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  ExitToApp as ExitIcon,
  PersonRemove as RemoveIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import {
  getTeamById,
  joinTeam,
  leaveTeam,
  updateTeam,
  removeMember,
  updateMemberRole
} from '../../services/teamService';
import { TeamWithMembers, UpdateTeamData } from '../../types/team';
import { useAuth } from '../../contexts/AuthContext';

function TeamDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const [team, setTeam] = useState<TeamWithMembers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState<UpdateTeamData>({});
  const navigate = useNavigate();
  const { authState } = useAuth();
  const user = authState.user;

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const teamData = await getTeamById(Number(id));
      setTeam(teamData);
      if (teamData) {
        setEditData({
          name: teamData.name,
          description: teamData.description,
          maxMembers: teamData.maxMembers
        });
      }
    } catch (err) {
      console.error('Error loading team:', err);
      setError('Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const handleJoinTeam = async () => {
    try {
      const result = await joinTeam(Number(id));
      setSuccess(result.message);
      loadTeam();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to join team');
    }
  };

  const handleLeaveTeam = async () => {
    try {
      const result = await leaveTeam(Number(id));
      setSuccess(result.message);
      loadTeam();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to leave team');
    }
  };

  const handleUpdateTeam = async () => {
    try {
      await updateTeam(Number(id), editData);
      setEditDialogOpen(false);
      setSuccess('Team updated successfully');
      loadTeam();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to update team');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    try {
      const result = await removeMember(Number(id), userId);
      setSuccess(result.message);
      loadTeam();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to remove member');
    }
  };

  const handlePromoteToCaptain = async (userId: number) => {
    try {
      const result = await updateMemberRole(Number(id), userId, 'captain');
      setSuccess(result.message);
      loadTeam();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to update role');
    }
  };

  const isUserMember = team?.members.some(m => m.userId === user?.id);
  const isUserCaptain = team?.members.some(m => m.userId === user?.id && m.role === 'captain');
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canEdit = isUserCaptain || isAdmin;

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </MainLayout>
    );
  }

  if (!team) {
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">Team not found</Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/student/teams')} sx={{ mt: 2 }}>
            Back to Teams
          </Button>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/student/teams')} sx={{ mb: 2 }}>
          Back to Teams
        </Button>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupsIcon sx={{ fontSize: 40 }} />
              {team.name}
            </Typography>
            <Box>
              {canEdit && (
                <IconButton onClick={() => setEditDialogOpen(true)} color="primary">
                  <EditIcon />
                </IconButton>
              )}
              {isUserMember ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ExitIcon />}
                  onClick={handleLeaveTeam}
                >
                  Leave Team
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleJoinTeam}
                >
                  Join Team
                </Button>
              )}
            </Box>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {team.description || 'No description'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Chip label={`${team.memberCount} / ${team.maxMembers} members`} />
            <Chip label={team.isActive ? 'Active' : 'Inactive'} color={team.isActive ? 'success' : 'default'} />
          </Box>
        </Paper>

        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Team Members
          </Typography>
          <List>
            {team.members.map((member) => (
              <ListItem
                key={member.id}
                secondaryAction={
                  canEdit && member.userId !== user?.id && (
                    <Box>
                      {member.role !== 'captain' && (
                        <IconButton edge="end" onClick={() => handlePromoteToCaptain(member.userId)}>
                          <StarIcon />
                        </IconButton>
                      )}
                      <IconButton edge="end" onClick={() => handleRemoveMember(member.userId)}>
                        <RemoveIcon />
                      </IconButton>
                    </Box>
                  )
                }
              >
                <ListItemAvatar>
                  <Avatar>{member.user.firstName[0]}{member.user.lastName[0]}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {member.user.firstName} {member.user.lastName}
                      {member.role === 'captain' && (
                        <Chip label="Captain" size="small" color="primary" icon={<StarIcon />} />
                      )}
                    </Box>
                  }
                  secondary={member.user.profile?.school || member.user.email}
                />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Edit Team Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Team</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Team Name"
              type="text"
              fullWidth
              variant="outlined"
              value={editData.name || ''}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={editData.description || ''}
              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Max Members"
              type="number"
              fullWidth
              variant="outlined"
              value={editData.maxMembers || 10}
              onChange={(e) => setEditData({ ...editData, maxMembers: parseInt(e.target.value) })}
              inputProps={{ min: 2, max: 50 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateTeam} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
}

export default TeamDetailsPage;
